/**
 * useTermsConditions Hooks
 * React Query hooks for Terms and Conditions with offline support
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { TermsConditionsService, getCurrentPlatform } from '../../services/termsConditionsService';
import {
  TCStatus,
  TCContent,
  AcceptanceInfo,
  TCVersion,
  CreateTCInput,
  UpdateTCInput,
  TCVersionStats,
} from '../../types/termsConditions';

// Cache time configuration for offline support
// gcTime (garbage collection time) determines how long inactive data stays in cache
const TC_GC_TIME = 7 * 24 * 60 * 60 * 1000; // 7 days - keep T&C cached for extended offline periods

// Query keys for Terms and Conditions
export const tcKeys = {
  all: ['termsConditions'] as const,
  status: () => [...tcKeys.all, 'status'] as const,
  current: () => [...tcKeys.all, 'current'] as const,
  acceptanceInfo: () => [...tcKeys.all, 'acceptanceInfo'] as const,
  // Admin keys
  admin: {
    all: ['termsConditions', 'admin'] as const,
    versions: () => [...tcKeys.admin.all, 'versions'] as const,
    stats: (tcId: number) => [...tcKeys.admin.all, 'stats', tcId] as const,
  },
};

interface UseTCStatusOptions extends Omit<UseQueryOptions<TCStatus, Error>, 'queryKey' | 'queryFn'> {
  enabled?: boolean;
}

/**
 * Hook to check if the current user needs to accept T&C
 * Cross-platform: once accepted on any platform, valid everywhere
 * Supports offline mode with cached status
 */
export const useTCStatus = (options?: UseTCStatusOptions) => {
  return useQuery<TCStatus, Error>({
    queryKey: tcKeys.status(),
    queryFn: () => TermsConditionsService.getStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: TC_GC_TIME, // Keep cached for 7 days for offline support
    networkMode: 'offlineFirst', // Try to use cached data when offline
    retry: 2,
    ...options,
  });
};

interface UseCurrentTCOptions extends Omit<UseQueryOptions<TCContent, Error>, 'queryKey' | 'queryFn'> {
  enabled?: boolean;
}

/**
 * Hook to get the current active Terms and Conditions content
 * Supports offline mode with cached T&C content
 */
export const useCurrentTC = (options?: UseCurrentTCOptions) => {
  return useQuery<TCContent, Error>({
    queryKey: tcKeys.current(),
    queryFn: () => TermsConditionsService.getCurrentTC(),
    staleTime: 30 * 60 * 1000, // 30 minutes (T&C content rarely changes)
    gcTime: TC_GC_TIME, // Keep cached for 7 days for offline support
    networkMode: 'offlineFirst', // Try to use cached data when offline
    retry: 2,
    ...options,
  });
};

interface UseAcceptanceInfoOptions extends Omit<UseQueryOptions<AcceptanceInfo, Error>, 'queryKey' | 'queryFn'> {
  enabled?: boolean;
}

/**
 * Hook to get the user's T&C acceptance information
 * Cross-platform: returns acceptance info regardless of which platform it was accepted on
 * Supports offline mode with cached acceptance info
 */
export const useAcceptanceInfo = (options?: UseAcceptanceInfoOptions) => {
  return useQuery<AcceptanceInfo, Error>({
    queryKey: tcKeys.acceptanceInfo(),
    queryFn: () => TermsConditionsService.getAcceptanceInfo(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: TC_GC_TIME, // Keep cached for 7 days for offline support
    networkMode: 'offlineFirst', // Try to use cached data when offline
    retry: 2,
    ...options,
  });
};

/**
 * Hook to accept Terms and Conditions
 * Records acceptance with the current platform for audit purposes
 * Note: Requires network connectivity - cannot be done offline
 */
export const useAcceptTC = () => {
  const queryClient = useQueryClient();
  const platform = getCurrentPlatform();

  return useMutation({
    mutationFn: async (tcId: number) => {
      await TermsConditionsService.acceptTC(tcId, platform);
      return { tcId, platform };
    },
    onSuccess: () => {
      // Invalidate T&C status and acceptance info to refetch
      queryClient.invalidateQueries({ queryKey: tcKeys.status() });
      queryClient.invalidateQueries({ queryKey: tcKeys.acceptanceInfo() });
    },
    onError: (error) => {
      console.error('Failed to accept Terms and Conditions:', error);
    },
    // Acceptance must be online - don't retry when offline
    networkMode: 'online',
  });
};

// ============================================
// Admin Hooks (IT Administrator only)
// ============================================

interface UseAllVersionsOptions extends Omit<UseQueryOptions<TCVersion[], Error>, 'queryKey' | 'queryFn'> {
  enabled?: boolean;
}

/**
 * Hook to get all T&C versions (IT Admin only)
 */
export const useAllTCVersions = (options?: UseAllVersionsOptions) => {
  return useQuery<TCVersion[], Error>({
    queryKey: tcKeys.admin.versions(),
    queryFn: () => TermsConditionsService.getAllVersions(),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: 2,
    ...options,
  });
};

/**
 * Hook to create a new T&C version (IT Admin only)
 */
export const useCreateTCVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTCInput) => {
      return TermsConditionsService.createVersion(input);
    },
    onSuccess: () => {
      // Invalidate versions list to refetch
      queryClient.invalidateQueries({ queryKey: tcKeys.admin.versions() });
    },
    onError: (error) => {
      console.error('Failed to create T&C version:', error);
    },
  });
};

/**
 * Hook to update a T&C version (IT Admin only)
 */
export const useUpdateTCVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tcId, input }: { tcId: number; input: UpdateTCInput }) => {
      return TermsConditionsService.updateVersion(tcId, input);
    },
    onSuccess: () => {
      // Invalidate versions list and current to refetch
      queryClient.invalidateQueries({ queryKey: tcKeys.admin.versions() });
      queryClient.invalidateQueries({ queryKey: tcKeys.current() });
    },
    onError: (error) => {
      console.error('Failed to update T&C version:', error);
    },
  });
};

/**
 * Hook to publish/activate a T&C version (IT Admin only)
 * WARNING: This will force ALL users to re-accept the terms
 */
export const usePublishTCVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tcId: number) => {
      await TermsConditionsService.publishVersion(tcId);
      return tcId;
    },
    onSuccess: () => {
      // Invalidate all T&C related queries
      queryClient.invalidateQueries({ queryKey: tcKeys.all });
      queryClient.invalidateQueries({ queryKey: tcKeys.status() });
      queryClient.invalidateQueries({ queryKey: tcKeys.current() });
    },
    onError: (error) => {
      console.error('Failed to publish T&C version:', error);
    },
  });
};

interface UseVersionStatsOptions extends Omit<UseQueryOptions<TCVersionStats, Error>, 'queryKey' | 'queryFn'> {
  enabled?: boolean;
}

/**
 * Hook to get acceptance statistics for a T&C version (IT Admin only)
 */
export const useVersionStats = (tcId: number, options?: UseVersionStatsOptions) => {
  return useQuery<TCVersionStats, Error>({
    queryKey: tcKeys.admin.stats(tcId),
    queryFn: () => TermsConditionsService.getVersionStats(tcId),
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
    enabled: tcId > 0,
    ...options,
  });
};
